/**
 * RevenueDashboard — the "where is money coming from?" page.
 *
 * Every figure comes from ONE endpoint, /api/finance/revenue/overview, which
 * reads each stream from its authoritative collection exactly once (event
 * finance, bookshop, Ministry Shop, campaign donations, plus whatever else is
 * in the bank ledger) and skips the bank-ledger mirrors so nothing is counted
 * twice. Labels and provenance are supplied by the server.
 *
 * Platform-wide money — the route is `platformOnly` and every endpoint behind
 * it is guarded by `require_platform_admin`.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WeeklyDigestCard } from '@/components/finance/WeeklyDigestCard';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, Calendar, DollarSign, Heart, Video, GraduationCap, Ticket, BookOpen, Banknote, Download, Receipt, Plus, Store, CreditCard } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Cell,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency as fmtCurrency } from '@/lib/currency';

// Icon + colour per stream key. Keys match the `source_module` values the
// backend actually stores (see routes/revenue_overview.py LABELS) — the old
// map used names that no longer existed, so nearly every row rendered as a
// grey "Other".
const SOURCE_META = {
  donations:        { icon: Heart,         color: '#10b981' },
  event_finance:    { icon: Ticket,        color: '#f59e0b' },
  events:           { icon: Ticket,        color: '#f59e0b' },
  bookshop:         { icon: BookOpen,      color: '#ec4899' },
  ministry_shop:    { icon: Store,         color: '#d946ef' },
  school_fees:      { icon: GraduationCap, color: '#3b82f6' },
  studio_bookings:  { icon: Video,         color: '#8b5cf6' },
  pos_sales:        { icon: Banknote,      color: '#0ea5e9' },
  external_payment: { icon: CreditCard,    color: '#14b8a6' },
  stripe:           { icon: CreditCard,    color: '#6366f1' },
  payment_plan:     { icon: CreditCard,    color: '#8b5cf6' },
  wifi:             { icon: Banknote,      color: '#0891b2' },
  lab_pc:           { icon: Banknote,      color: '#7c3aed' },
  manual:           { icon: Banknote,      color: '#64748b' },
  manual_expense:   { icon: Receipt,       color: '#f43f5e' },
  procurement_po:   { icon: Receipt,       color: '#e11d48' },
  pos_sales_refund: { icon: Receipt,       color: '#fb7185' },
  uncategorised:    { icon: Banknote,      color: '#64748b' },
};
const FALLBACK_META = { icon: Banknote, color: '#64748b' };

// Canonical formatter — safe for non-ISO codes (ZWG / ZIG) and keeps the
// code visible so a mixed-currency dashboard stays unambiguous.
const fmt = (n, c = 'USD') => fmtCurrency(n, c, { showCode: true });

// Builds and downloads a CSV summarising the current dashboard view.
// Two sections: (1) per-source totals, (2) daily trend.
function exportRevenueCsv({ data, currency, days, sources, trend }) {
  const lines = [];
  const ts = new Date().toISOString().slice(0, 10);
  lines.push(`Revenue Dashboard,${currency},last ${days} days,exported ${ts}`);
  lines.push('');
  lines.push('-- By source --');
  lines.push('source_module,label,total,currency,transaction_count,read_from');
  for (const s of sources) {
    lines.push([s.key, csv(s.label), Number(s.total || 0).toFixed(2), s.currency, s.count || 0, s.source].join(','));
  }
  lines.push('');
  lines.push('-- Daily trend --');
  lines.push('date,total,currency');
  for (const d of trend) {
    lines.push([d.date, Number(d.total || 0).toFixed(2), currency].join(','));
  }
  lines.push('');
  lines.push('-- Currency totals --');
  for (const [c, total] of Object.entries(data?.income_totals_by_currency || {})) {
    lines.push(`${c},${Number(total || 0).toFixed(2)}`);
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `revenue-${currency}-${days}d-${ts}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
const csv = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const RevenueDashboard = () => {
  const [days, setDays] = useState(30);
  const [currency, setCurrency] = useState('USD');
  const [eventId, setEventId] = useState('');  // '' = all events
  const [expenseOpen, setExpenseOpen] = useState(false);
  const qc = useQueryClient();

  // One assembler for every stream — de-duplicated server side.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['revenue-overview', days, currency],
    // No currency filter — the endpoint returns every currency so the
    // "all currencies" strip and the FX grand total have something to show;
    // the per-currency view is a client-side filter on the same payload.
    queryFn: () => api.get('/finance/revenue/overview', {
      params: { days, convert_to: currency, approved_only: true },
    }).then(r => r.data),
  });

  // Profit & Loss view (income vs expense) and per-tenant breakdown.
  const { data: pnl } = useQuery({
    queryKey: ['revenue-pnl', days, currency],
    queryFn: () => api.get(`/bank/pnl?days=${days}&currency=${currency}`).then(r => r.data),
  });
  const { data: tenants } = useQuery({
    queryKey: ['revenue-tenants-breakdown', days],
    queryFn: () => api.get(`/bank/tenants-breakdown?days=${days}`).then(r => r.data).catch(() => []),
  });

  // Per-event finance detail — only needed for the per-event table and the
  // event filter, so it stays on the finance module's own summary endpoint.
  const { data: eventFin } = useQuery({
    queryKey: ['revenue-event-finance', eventId, currency, days],
    queryFn: () =>
      api.get(`/finance/revenue-summary`, {
        params: {
          event_id: eventId || undefined,
          currency,
          days,
          approved_only: true,
          // Read the unified ledger rather than the legacy union path, so this
          // matches what the Ledger Explorer shows.
          source: 'ledger',
        },
      }).then(r => r.data).catch(() => null),
  });

  // Available events (for filter dropdown)
  const { data: eventsList = [] } = useQuery({
    queryKey: ['revenue-events-list'],
    queryFn: () => api.get('/admin/events').then(r => r.data).catch(() => []),
  });

  // Full ecosystem-wide currency list (73+ currencies) — powers the dropdown
  const { data: currencyMeta } = useQuery({
    queryKey: ['currency-list-public'],
    queryFn: () => api.get('/currency/list').then(r => r.data).catch(() => null),
    staleTime: 5 * 60 * 1000,
  });
  const allCurrencies = React.useMemo(
    () => (currencyMeta?.currencies || []).map(c => c.code),
    [currencyMeta]
  );

  // Income streams for the selected currency (labels come from the server).
  const sources = useMemo(() => {
    if (!data?.income_streams) return [];
    return data.income_streams
      .filter(s => s.currency === currency)
      .map(s => {
        const meta = SOURCE_META[s.key] || FALLBACK_META;
        return { ...s, color: meta.color, icon: meta.icon };
      });
  }, [data, currency]);

  const expenseStreams = useMemo(() => {
    if (!data?.expense_streams) return [];
    return data.expense_streams
      .filter(s => s.currency === currency)
      .map(s => {
        const meta = SOURCE_META[s.key] || FALLBACK_META;
        return { ...s, color: meta.color, icon: meta.icon };
      });
  }, [data, currency]);

  // Re-shape by_day for the line chart (single currency)
  const trend = useMemo(() => {
    if (!data?.by_day) return [];
    return data.by_day
      .filter(d => d.currency === currency)
      .map(d => ({ date: d.date.slice(5), total: d.total }));   // MM-DD
  }, [data, currency]);

  // Headline figures — all from the de-duplicated overview.
  const totalForCurrency = data?.income_totals_by_currency?.[currency] || 0;
  const totalExpenseForCurrency = data?.expense_totals_by_currency?.[currency] || 0;
  const netRevenue = data?.net_totals_by_currency?.[currency]
    ?? (totalForCurrency - totalExpenseForCurrency);
  const donationsTotal = data?.donations_totals_by_currency?.[currency] || 0;
  // Counts follow the selected currency so they line up with the figures
  // beside them (the payload also carries ecosystem-wide totals).
  const incomeTxnCount = sources.reduce((n, s) => n + (s.count || 0), 0);
  const expenseTxnCount = expenseStreams.reduce((n, s) => n + (s.count || 0), 0);

  // Pending items across every currency — surface as a banner so admins
  // understand why their headline totals may read 0 (they haven't approved).
  const pendingIncomeByCur = data?.pending_income_by_currency || {};
  const pendingExpenseByCur = data?.pending_expense_by_currency || {};
  const totalPendingIncomeCount = Object.keys(pendingIncomeByCur).length;
  const totalPendingExpenseCount = Object.keys(pendingExpenseByCur).length;
  const hasPending = totalPendingIncomeCount > 0 || totalPendingExpenseCount > 0;

  // Live FX grand total: sum of every currency, converted into the selected display currency
  const convertedTotals = data?.converted_totals;

  // Union of currencies from ALL sources (73+ from public endpoint + any currencies we've seen data in).
  const currencyOptions = useMemo(() => {
    const set = new Set(['USD']);
    allCurrencies.forEach(c => set.add(c));
    Object.keys(data?.income_totals_by_currency || {}).forEach(c => set.add(c));
    Object.keys(data?.expense_totals_by_currency || {}).forEach(c => set.add(c));
    Object.keys(pendingIncomeByCur).forEach(c => set.add(c));
    Object.keys(pendingExpenseByCur).forEach(c => set.add(c));
    return Array.from(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, allCurrencies]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="revenue-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
              Revenue Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every dollar coming in, organised by where it came from.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="text-sm border rounded h-9 px-2 bg-white max-w-[200px]"
              data-testid="revenue-event-filter"
            >
              <option value="">All events</option>
              {eventsList.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.title || ev.name || ev.id}</option>
              ))}
            </select>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="text-sm border rounded h-9 px-2 bg-white"
              data-testid="revenue-currency"
            >
              {currencyOptions.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="text-sm border rounded h-9 px-2 bg-white"
              data-testid="revenue-range"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => exportRevenueCsv({ data, currency, days, sources, trend })}
              disabled={!data}
              data-testid="revenue-export-csv"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            {eventId && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const base = process.env.REACT_APP_BACKEND_URL;
                  window.open(
                    `${base}/api/admin/events/${eventId}/finance/export.csv?kind=all&approved_only=true`,
                    '_blank'
                  );
                }}
                data-testid="revenue-export-event-csv"
                title="Export finance ledger for the selected event"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> Event CSV
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.print()}
              disabled={!data}
              data-testid="revenue-export-pdf"
              title="Use your browser's Print → Save as PDF"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
            <Button
              size="sm"
              onClick={() => setExpenseOpen(true)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="revenue-record-expense"
            >
              <Receipt className="h-3.5 w-3.5 mr-1" /> Record Expense
            </Button>
          </div>
        </div>

        {expenseOpen && (
          <RecordExpenseDialog
            defaultCurrency={currency}
            currencies={currencyOptions}
            onClose={() => setExpenseOpen(false)}
            onSaved={() => {
              setExpenseOpen(false);
              qc.invalidateQueries({ queryKey: ['revenue-overview'] });
              qc.invalidateQueries({ queryKey: ['revenue-pnl'] });
              qc.invalidateQueries({ queryKey: ['revenue-tenants-breakdown'] });
              qc.invalidateQueries({ queryKey: ['revenue-event-finance'] });
              toast.success('Expense recorded');
            }}
          />
        )}

        {isError && (
          <Card>
            <CardContent className="py-12 text-center text-slate-500 text-sm">
              Could not load revenue data. You may not have permission, or no transactions exist yet.
            </CardContent>
          </Card>
        )}

        {!isError && (
          <div className="space-y-4">
            <WeeklyDigestCard currency={currency} />
            {/* Pending approval banner — explains why headline totals may read 0 */}
            {hasPending && (
              <Card className="border-amber-300 bg-amber-50" data-testid="pending-approval-banner">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="mt-0.5 p-2 rounded-lg bg-amber-100 text-amber-700">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-amber-900 text-sm">
                      You have pending items awaiting approval
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      Only approved income and expenses count toward Revenue. Approve pending rows in <strong>Events → Finance → Approve</strong> to make them appear here.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      {Object.entries(pendingIncomeByCur).map(([c, v]) => (
                        <span key={`pi-${c}`} className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-800" data-testid={`pending-income-chip-${c}`}>
                          +{fmt(v, c)} income
                        </span>
                      ))}
                      {Object.entries(pendingExpenseByCur).map(([c, v]) => (
                        <span key={`pe-${c}`} className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-800" data-testid={`pending-expense-chip-${c}`}>
                          -{fmt(v, c)} expense
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grand total across all currencies (live FX) */}
            {convertedTotals && !convertedTotals.error && (
              (convertedTotals.total_income || 0) + (convertedTotals.total_expenses || 0) > 0
            ) && (
              <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="converted-grand-total-card">
                <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="p-2 rounded-lg bg-emerald-500 text-white">
                    <DollarSign className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0 sm:min-w-[180px]">
                    <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                      Grand total (all currencies → {convertedTotals.target_currency})
                    </p>
                    <p className="text-2xl font-bold text-emerald-900" data-testid="converted-grand-net">
                      {fmt(convertedTotals.net_revenue, convertedTotals.target_currency)}
                    </p>
                    <p className="text-[11px] text-emerald-700 mt-0.5">
                      Income {fmt(convertedTotals.total_income, convertedTotals.target_currency)}
                      {' · '}Expenses {fmt(convertedTotals.total_expenses, convertedTotals.target_currency)}
                      {' · '}Rates: <span className="capitalize">{convertedTotals.rate_source}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top stats — Live Financial Summary (bank ledger + event finance) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card data-testid="revenue-summary-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-emerald-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Revenue ({currency})</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="revenue-total">{fmt(totalForCurrency, currency)}</p>
                  <p className="text-xs text-slate-500">
                    {sources.length} stream{sources.length === 1 ? '' : 's'} · {incomeTxnCount} transaction{incomeTxnCount === 1 ? '' : 's'}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="expenses-summary-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-rose-600 mb-1">
                    <TrendingUp className="h-4 w-4 rotate-180" />
                    <span className="text-xs uppercase tracking-wide font-medium">Expenses ({currency})</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="revenue-expenses-total">
                    {fmt(totalExpenseForCurrency, currency)}
                  </p>
                  <p className="text-xs text-slate-500">
                    approved expenditure · {expenseTxnCount} entr{expenseTxnCount === 1 ? 'y' : 'ies'}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="net-revenue-summary-card">
                <CardContent className="p-4">
                  <div className={`flex items-center gap-2 ${netRevenue >= 0 ? 'text-emerald-600' : 'text-rose-600'} mb-1`}>
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Net Revenue ({currency})</span>
                  </div>
                  <p className={`text-2xl font-bold ${netRevenue >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="revenue-net-total">
                    {fmt(netRevenue, currency)}
                  </p>
                  <p className="text-xs text-slate-500">revenue − expenses</p>
                </CardContent>
              </Card>
              <Card data-testid="donations-summary-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-pink-600 mb-1">
                    <Heart className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Donations ({currency})</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="revenue-donations-total">
                    {fmt(donationsTotal, currency)}
                  </p>
                  <p className="text-xs text-slate-500">approved donations only</p>
                </CardContent>
              </Card>
            </div>

            {/* Multi-currency totals strip */}
            {Object.keys(data?.income_totals_by_currency || {}).length > 1 && (
              <Card data-testid="multi-currency-totals">
                <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                  <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold mr-2">
                    All currencies
                  </span>
                  {currencyOptions.map(c => {
                    const inc = data?.income_totals_by_currency?.[c] || 0;
                    const exp = data?.expense_totals_by_currency?.[c] || 0;
                    if (inc === 0 && exp === 0) return null;
                    return (
                      <button
                        key={c}
                        onClick={() => setCurrency(c)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${c === currency ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                        data-testid={`multi-currency-chip-${c}`}
                      >
                        <span className="font-semibold">{c}</span>
                        {' · '}
                        {fmt(inc - exp, c)}
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Secondary stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Income Transactions</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="revenue-txn-count">{incomeTxnCount}</p>
                  <p className="text-xs text-slate-500">{days}-day window</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-violet-600 mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Top Source</span>
                  </div>
                  <p className="text-lg font-semibold text-slate-900 truncate" data-testid="revenue-top-source">
                    {sources[0]?.label || '—'}
                  </p>
                  <p className="text-xs text-slate-500">{sources[0] ? fmt(sources[0].total, currency) : 'No data'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-600 mb-1">
                    <Banknote className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide font-medium">Streams</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="revenue-stream-count">{sources.length}</p>
                  <p className="text-xs text-slate-500">active income sources</p>
                </CardContent>
              </Card>
            </div>

            {/* Where the money went — every expense stream, both ledgers */}
            {expenseStreams.length > 0 && (
              <ExpenseStreamsPanel streams={expenseStreams} currency={currency} />
            )}

            {/* Per-event finance breakdown */}
            {(eventFin?.by_event || []).length > 0 && (
              <EventsFinanceBreakdown rows={eventFin.by_event.filter(r => r.currency === currency)} />
            )}

            {/* Profit & Loss row */}
            <PnlPanel pnl={pnl} currency={currency} days={days} />

            {/* Per-tenant breakdown (super admin only — silent fall-back when forbidden) */}
            {tenants && tenants.length > 0 && (
              <TenantsBreakdownPanel tenants={tenants} />
            )}

            {/* Trend line */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Daily Revenue Trend</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {trend.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-sm">
                    No deposits in this window. Run a campaign or close a studio booking to see data here.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" fontSize={11} />
                      <Tooltip
                        formatter={(v) => fmt(v, currency)}
                        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                      />
                      <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">By Source (chart)</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  {sources.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">No revenue yet.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(180, sources.length * 36)}>
                      <BarChart data={sources} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" stroke="#64748b" fontSize={11} />
                        <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={130} />
                        <Tooltip
                          formatter={(v) => fmt(v, currency)}
                          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                        />
                        <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                          {sources.map(s => (
                            <Cell key={`${s.key}-${s.currency}`} fill={s.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">By Source (detail)</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {sources.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">No revenue yet.</div>
                  ) : (
                    <div className="divide-y">
                      {sources.map(s => {
                        const Icon = s.icon;
                        return (
                          <div
                            key={`${s.key}-${s.currency}`}
                            className="p-3 flex items-center gap-3"
                            data-testid={`revenue-row-${s.key}`}
                          >
                            <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: s.color }}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{s.label}</p>
                              <p className="text-xs text-slate-500">
                                {s.count} transaction{s.count === 1 ? '' : 's'} · from <span className="font-mono">{s.source}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-slate-900">{fmt(s.total, s.currency)}</p>
                              <Badge variant="outline" className="text-[10px] mt-0.5">
                                {((s.total / Math.max(totalForCurrency, 1)) * 100).toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueDashboard;


function ExpenseStreamsPanel({ streams, currency }) {
  const grand = streams.reduce((sum, r) => sum + (r.total || 0), 0);
  return (
    <Card data-testid="expense-streams-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4 text-rose-600" />
          Expenses by Source · {fmt(grand, currency)}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {streams.map((r) => {
            const Icon = r.icon;
            return (
              <div key={`${r.key}-${r.currency}`} className="p-3 flex items-center gap-3" data-testid={`expense-row-${r.key}`}>
                <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: r.color }}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{r.label}</p>
                  <p className="text-xs text-slate-500">
                    {r.count} entr{r.count === 1 ? 'y' : 'ies'} · from <span className="font-mono">{r.source}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">{fmt(r.total, r.currency)}</p>
                  <Badge variant="outline" className="text-[10px] mt-0.5">
                    {((r.total / Math.max(grand, 1)) * 100).toFixed(1)}%
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


function EventsFinanceBreakdown({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card data-testid="events-finance-breakdown">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Ticket className="h-4 w-4 text-amber-600" />
          Per-event finance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide border-b">
                <th className="text-left py-2 px-2">Event</th>
                <th className="text-right py-2 px-2">Income</th>
                <th className="text-right py-2 px-2">Expense</th>
                <th className="text-right py-2 px-2">Net</th>
                <th className="text-right py-2 px-2">Cur</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((r, i) => (
                <tr key={`${r.event_id}-${r.currency}-${i}`} className="border-b last:border-0" data-testid={`events-finance-row-${r.event_id}`}>
                  <td className="py-2 px-2 text-slate-800 font-medium">{r.event_title || r.event_id}</td>
                  <td className="py-2 px-2 text-right text-emerald-700">{fmt(r.income, r.currency)}</td>
                  <td className="py-2 px-2 text-right text-rose-700">{fmt(r.expense, r.currency)}</td>
                  <td className={`py-2 px-2 text-right font-bold ${r.net >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>{fmt(r.net, r.currency)}</td>
                  <td className="py-2 px-2 text-right text-xs text-slate-400">{r.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


function PnlPanel({ pnl, currency, days }) {
  if (!pnl) return null;
  const income = pnl.income?.totals_by_currency?.[currency] || 0;
  const expense = pnl.expense?.totals_by_currency?.[currency] || 0;
  const net = pnl.net_by_currency?.[currency] || 0;
  const netPositive = net >= 0;
  const incomeRows = (pnl.income?.rows || []).filter(r => r.currency === currency);
  const expenseRows = (pnl.expense?.rows || []).filter(r => r.currency === currency);

  return (
    <Card data-testid="pnl-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-600" />
            Profit &amp; Loss · last {days} days · {currency}
          </CardTitle>
          <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${netPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="pnl-net">
            Net: {fmt(net, currency)}
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Income</span>
            <span className="text-sm font-bold text-emerald-800" data-testid="pnl-income-total">{fmt(income, currency)}</span>
          </div>
          {incomeRows.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No income in this window.</p>
          ) : (
            <ul className="space-y-1">
              {incomeRows.slice(0, 8).map((r) => (
                <li key={`i-${r.category}`} className="flex justify-between text-xs py-1 border-b border-slate-100">
                  <span className="text-slate-600 capitalize">{(r.category || 'uncategorised').replace(/_/g, ' ')}</span>
                  <span className="font-medium text-slate-800">{fmt(r.total, currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Expense</span>
            <span className="text-sm font-bold text-rose-800" data-testid="pnl-expense-total">{fmt(expense, currency)}</span>
          </div>
          {expenseRows.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No expenses recorded. Use the bank's "Record expense" endpoint to add one.</p>
          ) : (
            <ul className="space-y-1">
              {expenseRows.slice(0, 8).map((r) => (
                <li key={`e-${r.category}`} className="flex justify-between text-xs py-1 border-b border-slate-100">
                  <span className="text-slate-600 capitalize">{(r.category || 'uncategorised').replace(/_/g, ' ')}</span>
                  <span className="font-medium text-slate-800">{fmt(r.total, currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function TenantsBreakdownPanel({ tenants }) {
  return (
    <Card data-testid="tenants-breakdown">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-indigo-600" />
          Per-tenant P&amp;L breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide border-b">
                <th className="text-left py-2 px-2">Entity</th>
                <th className="text-left py-2 px-2">Type</th>
                <th className="text-right py-2 px-2">Income</th>
                <th className="text-right py-2 px-2">Expense</th>
                <th className="text-right py-2 px-2">Net</th>
                <th className="text-right py-2 px-2">Cur</th>
              </tr>
            </thead>
            <tbody>
              {tenants.slice(0, 20).map((t, i) => (
                <tr key={`${t.tenant_id}-${t.currency}-${i}`} className="border-b last:border-0" data-testid={`tenants-breakdown-row-${t.tenant_id}`}>
                  <td className="py-2 px-2 text-slate-800 font-medium">{t.tenant_name}</td>
                  <td className="py-2 px-2 text-xs text-slate-500 capitalize">{t.tenant_type || '—'}</td>
                  <td className="py-2 px-2 text-right text-emerald-700">{fmt(t.income, t.currency)}</td>
                  <td className="py-2 px-2 text-right text-rose-700">{fmt(t.expense, t.currency)}</td>
                  <td className={`py-2 px-2 text-right font-bold ${t.net >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>{fmt(t.net, t.currency)}</td>
                  <td className="py-2 px-2 text-right text-xs text-slate-400">{t.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// =========================================================
// RECORD EXPENSE DIALOG
// =========================================================
// Posts to /api/bank/expenses/request (bank_governance.py) — requires
// finance.edit, auto-creates an operating wallet for the tenant if one doesn't
// exist yet, and stamps expense_category + vendor on the transaction so the
// P&L panel groups it correctly. Anything at or above the approval threshold
// queues for a second approver instead of posting. Tenant id is optional —
// leave blank to use the caller's own tenant scope.
const EXPENSE_CATEGORIES = [
  { value: 'rent',         label: 'Rent / Lease' },
  { value: 'utilities',    label: 'Utilities (water, power, internet)' },
  { value: 'salaries',     label: 'Salaries / Wages' },
  { value: 'supplies',     label: 'Office supplies / Stationery' },
  { value: 'equipment',    label: 'Equipment purchase' },
  { value: 'maintenance',  label: 'Maintenance / Repairs' },
  { value: 'marketing',    label: 'Marketing / Advertising' },
  { value: 'transport',    label: 'Transport / Fuel' },
  { value: 'food',         label: 'Food / Catering' },
  { value: 'travel',       label: 'Travel / Accommodation' },
  { value: 'professional', label: 'Professional services (legal, audit, IT)' },
  { value: 'insurance',    label: 'Insurance' },
  { value: 'taxes',        label: 'Taxes / Levies' },
  { value: 'other',        label: 'Other' },
];

function RecordExpenseDialog({ defaultCurrency, currencies = [], onClose, onSaved }) {
  const currencyChoices = currencies.length ? currencies : ['USD'];
  const [form, setForm] = useState({
    amount: '',
    currency: defaultCurrency || 'USD',
    expense_category: 'supplies',
    description: '',
    vendor: '',
    tenant_id: '',
  });

  const mut = useMutation({
    mutationFn: () => api.post('/bank/expenses/request', {
      amount: parseFloat(form.amount) || 0,
      currency: form.currency,
      expense_category: form.expense_category,
      description: form.description,
      vendor: form.vendor,
      tenant_id: form.tenant_id || undefined,
    }).then(r => r.data),
    onSuccess: (d) => {
      if (d?.pending) {
        toast.success(d.message || 'Queued for a second approver');
      } else {
        toast.success('Expense recorded');
      }
      onSaved?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to record expense'),
  });

  const valid = parseFloat(form.amount) > 0 && form.expense_category;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="record-expense-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-4 w-4 text-rose-600" />Record Expense</DialogTitle>
          <DialogDescription>
            Logs a withdraw against the operating wallet so it appears in your Profit &amp; Loss
            expense column. Large expenses queue for a second approver before any money moves.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label>Amount *</Label>
              <Input
                type="number" step="0.01" min={0}
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                data-testid="expense-amount"
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="expense-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencyChoices.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.expense_category} onValueChange={v => setForm({ ...form, expense_category: v })}>
              <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vendor / Payee</Label>
            <Input
              value={form.vendor}
              onChange={e => setForm({ ...form, vendor: e.target.value })}
              placeholder="e.g. ZESA, Econet, John Doe"
              data-testid="expense-vendor"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What was this expense for?"
              data-testid="expense-description"
            />
          </div>
          <div>
            <Label>Tenant ID <span className="text-xs text-slate-500 font-normal">(required for super admins; leave blank to use your own tenant scope)</span></Label>
            <Input
              value={form.tenant_id}
              onChange={e => setForm({ ...form, tenant_id: e.target.value })}
              placeholder="e.g. 69d63c2c39ded375827179d9"
              className="font-mono text-xs"
              data-testid="expense-tenant"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              The expense will be posted to that tenant's operating wallet (auto-created in the chosen currency if needed).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mut.mutate()}
            disabled={!valid || mut.isPending}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="expense-save"
          >
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            <Plus className="h-3.5 w-3.5 mr-1" />Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
